/**
 * Explore Table view (POS2-884): the true pivot — date rows × split-value columns with per-row,
 * per-column, and grand totals. Uncapped columns (the shell fetches the uncapped pivot for this
 * view), sticky header, scrolls inside the panel. Unsplit → a single measure-value column.
 * Totals come straight from the engine margins, so they agree with the chart views by construction.
 * Each value carries a small period-over-period Δ beneath it (POS2-1038) — per split cell vs the
 * same column's previous period, and on the row total — from the tooltip's shared per-series delta.
 */
import { cn } from "@/lib/utils";
import { EXPLORE_MEASURES, GRAN_UNIT_KEYS, precedingWindow } from "@/lib/explore-engine";
import { buildTableMatrix, cellDeltas, rowTotalDeltas } from "@/lib/explore-chart";
import { useTranslation } from "@/i18n/use-translation";
import { formatPercent } from "@/lib/format";

import type { ExploreViewProps } from "./chart-parts";

/** The small ▲/▼ % under a value — red when waste rose, green when it fell (matches the panel pill
 * + tooltip). The line is ALWAYS rendered at a fixed height (h-4) — blank when there's no comparable
 * previous period — so every cell reserves the same height and a value with no Δ beneath it doesn't
 * look clipped against its two-line neighbours (Artem). */
function DeltaSub({ d, title }: { d: number | null; title: string }) {
  const { locale } = useTranslation();
  return (
    <div
      className={cn(
        "h-4 whitespace-nowrap text-[11px] font-medium leading-4 tabular-nums",
        d != null && (d > 0 ? "text-red-600" : "text-emerald-600"),
      )}
      title={d != null ? title : undefined}
    >
      {d != null ? `${d > 0 ? "▲" : "▼"} ${formatPercent(locale, Math.abs(d))}` : " "}
    </div>
  );
}

export function ExploreTable({
  pivot,
  priorPivot,
  measure,
  currency,
  gran,
  window,
  timezone,
}: ExploreViewProps) {
  const { locale, t } = useTranslation();
  const ctx = { currency, locale, t };
  const fmt = (n: number) => EXPLORE_MEASURES[measure].format(n, ctx);
  const { columns, rows, colTotals, grandTotal, unsplit } = buildTableMatrix(
    pivot,
    measure,
    gran,
    ctx,
  );
  // Δ vs the previous period (prev day/week/month), suppressed on partial edge buckets — beneath
  // each split cell (per column) and beneath the row total, from one shared derivation. The prior
  // pivot (the preceding window) seeds the FIRST row's Δ from the period just before the window.
  const priorWindow = precedingWindow(window);
  const rowDeltas = rowTotalDeltas(pivot, measure, window, gran, timezone, priorPivot, priorWindow);
  const cells = cellDeltas(pivot, measure, window, gran, timezone, priorPivot, priorWindow);
  const deltaTitle = t("waste.vsPrevious", { unit: t(GRAN_UNIT_KEYS[gran]) });
  const th = "px-3 py-2 text-xs font-medium uppercase tracking-wide text-gray-400";

  return (
    <div
      className="min-h-0 flex-1 overflow-auto rounded-md border border-black/[0.06] print:h-auto print:flex-none print:overflow-visible print:rounded-none print:border-0"
      data-testid="explore-view-table"
    >
      {/* table-fixed with per-column widths: equal-width value columns (a long header like
          POST_CONSUMER no longer widens its own column), and a fixed intrinsic width so the table
          keeps its size on a narrow screen and the container scrolls instead of cramming/overlapping
          the columns (mobile). min-w-full stretches it to fill a wide panel; the widths hold the
          floor below which it scrolls. */}
      <table className="min-w-full table-fixed border-collapse text-sm">
        <thead className="sticky top-0 z-10 bg-white">
          <tr className="border-b border-black/[0.06]">
            <th className={`${th} w-40 text-left`}>{t("waste.date")}</th>
            {columns.map((c) => (
              <th key={c.key} className={`${th} w-32 text-right`}>
                {/* Fill the cell (no max-w) so the header right-aligns with the numbers below it —
                    a capped span left-anchors and ends short of the right-aligned values. */}
                <span className="block truncate" title={c.label}>
                  {c.label}
                </span>
              </th>
            ))}
            <th className={`${th} w-32 text-right`}>
              {unsplit ? EXPLORE_MEASURES[measure].label(ctx) : t("waste.total")}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.key}
              className="break-inside-avoid border-b border-black/[0.06] hover:bg-black/[0.02]"
            >
              <td className="whitespace-nowrap px-3 py-1.5 align-top text-gray-700">{r.label}</td>
              {r.cells.map((v, i) => (
                <td
                  key={columns[i].key}
                  className="px-3 py-1.5 text-right align-top tabular-nums text-gray-700"
                >
                  <div>{fmt(v)}</div>
                  <DeltaSub
                    d={cells.get(`${r.key}|${columns[i].key}`) ?? null}
                    title={deltaTitle}
                  />
                </td>
              ))}
              <td className="px-3 py-1.5 text-right align-top font-medium tabular-nums text-gray-900">
                <div>{fmt(r.total)}</div>
                <DeltaSub d={rowDeltas.get(r.key) ?? null} title={deltaTitle} />
              </td>
            </tr>
          ))}
          {/* Totals as the last tbody row, NOT a <tfoot> — a tfoot repeats on every printed page. */}
          <tr className="break-inside-avoid border-t border-black/[0.06]">
            <td className="px-3 py-2 font-medium text-gray-900">{t("waste.total")}</td>
            {colTotals.map((v, i) => (
              <td
                key={columns[i].key}
                className="px-3 py-2 text-right font-medium tabular-nums text-gray-900"
              >
                {fmt(v)}
              </td>
            ))}
            <td
              className="px-3 py-2 text-right font-semibold tabular-nums text-gray-900"
              data-testid="explore-table-total"
            >
              {fmt(grandTotal)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
